import { createContext, useState, useEffect, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import api, { tokenStorage, ACCESS_TOKEN_KEY } from '../services/api'
import toast from 'react-hot-toast'

export const AuthContext = createContext(null)

// BUG-102: lê o sub (id do vendor) do payload do JWT, sem validar assinatura.
// Serve só para decidir se a aba recarrega; quem valida o token é a API.
// Qualquer falha devolve null, e a aba recarrega (lado seguro).
function getTokenSubject(token) {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(payload)).sub ?? null
  } catch {
    return null
  }
}

export function AuthProvider({ children }) {
  // BUG-100: o cache do React Query é da aba, não do vendor. Zerar sempre que a
  // identidade muda (login, cadastro, logout); senão o próximo vendor vê as listas
  // do anterior até o staleTime (5 min) vencer. O refresh que falha em api.js já
  // recarrega a página, o que também zera o cache.
  const queryClient = useQueryClient()
  const [user, setUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  // Verifica se usuário está autenticado ao carregar
  useEffect(() => {
    const initAuth = async () => {
      const accessToken = tokenStorage.getAccessToken()
      
      if (!accessToken) {
        setIsLoading(false)
        return
      }

      try {
        // Busca dados do usuário logado
        const response = await api.get('/api/auth/me')
        setUser(response.data.data.user)
      } catch (error) {
        // Token inválido, limpa storage
        tokenStorage.clearTokens()
        setUser(null)
      } finally {
        setIsLoading(false)
      }
    }

    initAuth()
  }, [])

  // BUG-102: o localStorage é compartilhado entre as abas, e o evento 'storage'
  // só dispara nas OUTRAS abas. Se outra aba sai ou entra com outro vendor, esta
  // recarrega: o initAuth refaz o /me com o token atual ou cai no login, e o
  // cache do React Query some junto. O refresh de token também regrava a chave,
  // mas com o mesmo sub; nesse caso nada muda.
  useEffect(() => {
    if (!user) return

    const handleStorage = (event) => {
      if (event.key !== null && event.key !== ACCESS_TOKEN_KEY) return
      const currentToken = tokenStorage.getAccessToken()
      if (currentToken && getTokenSubject(currentToken) === user.id) return
      window.location.reload()
    }

    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [user])

  // Login
  const login = useCallback(async (email, password) => {
    try {
      const response = await api.post('/api/auth/login', { email, password })
      const { vendor, accessToken, refreshToken } = response.data.data

      tokenStorage.setTokens(accessToken, refreshToken)
      queryClient.clear()
      setUser(vendor)
      
      toast.success(`Bem-vindo, ${vendor.name}!`)
      return { success: true }

    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao fazer login'
      const details = error.response?.data?.details || []
      
      if (details.length > 0) {
        details.forEach(detail => {
          toast.error(`${detail.field}: ${detail.message}`)
        })
      } else {
        toast.error(message)
      }
      
      return { success: false, error: message }
    }
  }, [queryClient])

  // Cadastro
  const register = useCallback(async (data) => {
    try {
      const response = await api.post('/api/auth/register', data)
      const { vendor, accessToken, refreshToken } = response.data.data

      tokenStorage.setTokens(accessToken, refreshToken)
      queryClient.clear()
      setUser(vendor)
      
      toast.success('Conta criada com sucesso!')
      return { success: true }

    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao criar conta'
      const details = error.response?.data?.details || []
      
      // Se tiver detalhes de validação, mostra cada um
      if (details.length > 0) {
        details.forEach(detail => {
          toast.error(`${detail.field}: ${detail.message}`)
        })
      } else {
        toast.error(message)
      }
      
      return { success: false, error: message, details }
    }
  }, [queryClient])

  // Logout
  const logout = useCallback(async () => {
    try {
      const refreshToken = tokenStorage.getRefreshToken()
      if (refreshToken) {
        await api.post('/api/auth/logout', { refreshToken })
      }
    } catch (error) {
      // Ignora erro, faz logout local mesmo assim
    } finally {
      tokenStorage.clearTokens()
      queryClient.clear()
      setUser(null)
      toast.success('Até logo!')
    }
  }, [queryClient])

  // Atualiza dados do usuário (após editar perfil)
  const updateUser = useCallback((newData) => {
    setUser((prev) => ({ ...prev, ...newData }))
  }, [])

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    updateUser,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
